import { X } from 'lucide-react';
import React, { useState } from 'react';
import type { SchemaDefinitionCreate } from '../lib/api';
import { MonacoJsonEditor } from './MonacoJsonEditor';

interface CreateSchemaDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: SchemaDefinitionCreate) => void;
  isLoading?: boolean;
}

const SCHEMA_CATEGORIES = [
  { value: 'contacts_schema', label: 'Contacts Schema' },
  { value: 'transactions_schema', label: 'Transactions Schema' },
  { value: 'locations_schema', label: 'Locations Schema' },
  { value: 'custom_schema', label: 'Custom Schema' },
];

const DEFAULT_SCHEMA = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  type: 'object',
  properties: {
    id: {
      type: 'string',
      description: 'Unique identifier',
    },
  },
  required: ['id'],
};

export const CreateSchemaDialog: React.FC<CreateSchemaDialogProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    pluralName: '',
    description: '',
    category: 'custom_schema',
    externalIdField: 'id',
    schema: JSON.stringify(DEFAULT_SCHEMA, null, 2),
  });
  const [isJsonValid, setIsJsonValid] = useState(true);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!isJsonValid) {
      return;
    }

    try {
      const parsedSchema = JSON.parse(formData.schema);
      const createData: SchemaDefinitionCreate = {
        name: formData.name,
        pluralName: formData.pluralName || formData.name + 's',
        description: formData.description,
        category: formData.category,
        externalIdField: formData.externalIdField,
        schema: parsedSchema,
      };

      onSubmit(createData);
    } catch (err) {
      // Error will be handled by parent component
      console.error('Failed to parse schema JSON:', err);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setFormData({
        name: '',
        pluralName: '',
        description: '',
        category: 'custom_schema',
        externalIdField: 'id',
        schema: JSON.stringify(DEFAULT_SCHEMA, null, 2),
      });
      setIsJsonValid(true);
      onClose();
    }
  };

  const isFormValid = () => {
    return (
      formData.name.trim() !== '' &&
      formData.description.trim() !== '' &&
      formData.category !== '' &&
      formData.externalIdField.trim() !== '' &&
      isJsonValid
    );
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={handleClose} />

      {/* Dialog */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">Create New Schema</h2>
            <button
              onClick={handleClose}
              disabled={isLoading}
              className="text-gray-400 hover:text-gray-500 disabled:opacity-50">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
            <div className="px-6 py-4 space-y-6">
              {/* Schema Category */}
              <div>
                <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-2">
                  Schema Type <span className="text-red-500">*</span>
                </label>
                <select
                  id="category"
                  value={formData.category}
                  onChange={e => setFormData({ ...formData, category: e.target.value })}
                  className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                  disabled={isLoading}>
                  {SCHEMA_CATEGORIES.map(cat => (
                    <option key={cat.value} value={cat.value}>
                      {cat.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Name */}
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  Schema Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  value={formData.name}
                  onChange={e => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., customer, transaction, location"
                  className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                  disabled={isLoading}
                />
              </div>

              {/* Plural Name */}
              <div>
                <label htmlFor="pluralName" className="block text-sm font-medium text-gray-700 mb-2">
                  Plural Name
                </label>
                <input
                  type="text"
                  id="pluralName"
                  value={formData.pluralName}
                  onChange={e => setFormData({ ...formData, pluralName: e.target.value })}
                  placeholder="e.g., customers (optional, will auto-generate)"
                  className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                  disabled={isLoading}
                />
              </div>

              {/* External ID Field */}
              <div>
                <label
                  htmlFor="externalIdField"
                  className="block text-sm font-medium text-gray-700 mb-2">
                  External ID Field <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="externalIdField"
                  value={formData.externalIdField}
                  onChange={e =>
                    setFormData({
                      ...formData,
                      externalIdField: e.target.value,
                    })
                  }
                  placeholder="e.g., id, customer_id"
                  className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                  disabled={isLoading}
                />
                <p className="mt-1 text-xs text-gray-500">
                  The field name that uniquely identifies records in your system
                </p>
              </div>

              {/* Description */}
              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={e => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  placeholder="Describe the purpose of this schema..."
                  className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                  disabled={isLoading}
                  required
                />
              </div>

              {/* Schema JSON */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  JSON Schema <span className="text-red-500">*</span>
                </label>
                <MonacoJsonEditor
                  value={formData.schema}
                  onChange={value => setFormData({ ...formData, schema: value })}
                  height="300px"
                  readOnly={isLoading}
                  onValidationChange={valid => {
                    setIsJsonValid(valid);
                  }}
                />
                <p className="mt-2 text-xs text-gray-500">
                  Define the JSON schema for your data structure. Make sure the external ID field exists
                  in the properties.
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end space-x-3 bg-gray-50">
              <button
                type="button"
                onClick={handleClose}
                disabled={isLoading}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50">
                Cancel
              </button>
              <button
                type="submit"
                disabled={!isFormValid() || isLoading}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed">
                {isLoading ? 'Creating...' : 'Create Schema'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
